import React, { useState, useMemo } from "react"
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  Button,
  Card,
  CardContent,
  Grid2,
  Chip,
  Alert,
  Skeleton,
  TextField,
  InputAdornment,
  LinearProgress
} from "@mui/material"
import {
  Close as CloseIcon,
  Search as SearchIcon,
  CheckCircle,
  Warning,
  Error,
  Inventory2,
  TrendingUp,
  MonetizationOn,
  QrCodeScanner,
  Refresh
} from "@mui/icons-material"
import {
  useGetAuditProductSummaryQuery,
  useGetAllAuditLotsQuery,
  FilterCondition,
  FilterOperator,
  LogicalOperator
  // AuditLotStatus
} from "src/graphql/quark/quark.type"
import { getDisplayPrice } from "src/shared/utils/helpers-functions"
import { DataGridMui } from "src/shared/container/data-grid-mui"
import { getProductLotsColumns } from "./getProductLotsColumns"
import { useTypedSelector } from "src/shared/store/store"

interface ProductLotsModalProps {
  open: boolean
  onClose: () => void
  auditProductId: string
  productName: string
}

const ProductLotsModal: React.FC<ProductLotsModalProps> = ({ open, onClose, auditProductId, productName }) => {
  const [searchTerm, setSearchTerm] = useState("")
  const currentWhLoc = useTypedSelector((state) => state.authentication.currentWhLoc)
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 })
  const [sorting, setSorting] = useState<{ id?: null | string; desc?: null | boolean }>({
    id: "actionId",
    desc: false
  })

  // Get product summary
  const { data: summaryData, loading: summaryLoading } = useGetAuditProductSummaryQuery({
    variables: { auditProductId },
    skip: !open || !auditProductId,
    fetchPolicy: "cache-and-network"
  })

  // Prepare filter conditions
  const filterConditions: FilterCondition[] = useMemo(() => {
    const conditions: FilterCondition[] = [
      {
        operator: FilterOperator.Equal,
        field: "auditProductId",
        value: auditProductId
      }
    ]

    if (searchTerm) {
      conditions.push({
        operator: FilterOperator.Like,
        field: "actionId",
        value: searchTerm
      })
    }

    return conditions
  }, [auditProductId, searchTerm])

  // Get lots data
  const {
    data: lotsData,
    loading: lotsLoading,
    refetch: refetchLots
  } = useGetAllAuditLotsQuery({
    variables: {
      locationId: currentWhLoc?.id,
      filter: {
        conditions: filterConditions,
        logicalOperator: LogicalOperator.And
      },
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize
    },
    skip: !open || !auditProductId,
    fetchPolicy: "cache-and-network"
  })

  const columns = useMemo(() => getProductLotsColumns(), [])
  const summary = summaryData?.getAuditProductSummary
  const lots = lotsData?.getAllAuditLots?.data || []

  // Calculate lot status summary
  // const lotStatusSummary = useMemo(() => {
  //   const matched = lots.filter((lot) => lot.status === AuditLotStatus.Matched).length
  //   const short = lots.filter((lot) => lot.status === AuditLotStatus.Short).length
  //   const excess = lots.filter((lot) => lot.status === AuditLotStatus.Excess).length
  //   const discrepancy = lots.filter((lot) => lot.status === AuditLotStatus.Discrepancy).length

  //   return { matched, short, excess, discrepancy }
  // }, [lots])

  // Calculate accuracy percentage
  const accuracyPercentage = summary?.expectedQuantity
    ? Math.round(((summary?.actualQuantity ?? 0) / (summary?.expectedQuantity ?? 0)) * 100)
    : 0

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setPagination({ pageIndex: 0, pageSize: pagination.pageSize })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth sx={{ "& .MuiDialog-paper": { height: "90vh" } }}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" component="div" fontWeight="bold">
              {summaryLoading ? <Skeleton width={300} /> : `${productName} - Lot Details`}
            </Typography>
            {!summaryLoading && summary && (
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Typography variant="body2" color="text.secondary">
                  {summary.totalLots} lots • {summary.scannedLots} scanned • {summary.remainingLots} remaining
                </Typography>
                <Chip
                  label={`${accuracyPercentage}% Accuracy`}
                  size="small"
                  color={accuracyPercentage >= 95 ? "success" : accuracyPercentage >= 80 ? "warning" : "error"}
                  variant="outlined"
                />
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {summaryLoading ? (
          <Box>
            {/* Summary Cards Skeleton */}
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              {[1, 2, 3, 4].map((index) => (
                <Grid2 size={{ xs: 12, sm: 6, md: 3 }} key={index}>
                  <Card>
                    <CardContent sx={{ textAlign: "center" }}>
                      <Skeleton variant="circular" width={32} height={32} sx={{ mx: "auto", mb: 1 }} />
                      <Skeleton variant="text" width={60} height={24} sx={{ mx: "auto" }} />
                      <Skeleton variant="text" width={100} height={16} sx={{ mx: "auto" }} />
                    </CardContent>
                  </Card>
                </Grid2>
              ))}
            </Grid2>

            {/* Lot Status Summary Skeleton */}
            <Box sx={{ mb: 3 }}>
              <Skeleton variant="text" width={150} height={24} sx={{ mb: 2 }} />
              <Grid2 container spacing={2}>
                {[1, 2, 3, 4].map((index) => (
                  <Grid2 size={{ xs: 6, sm: 3 }} key={index}>
                    <Skeleton variant="rectangular" height={60} />
                  </Grid2>
                ))}
              </Grid2>
            </Box>

            <Skeleton variant="rectangular" height={400} />
          </Box>
        ) : (
          <Box>
            {/* Summary Cards */}
            <Grid2 container spacing={2} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <Inventory2 color="primary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6">{summary?.expectedQuantity || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Expected Quantity
                    </Typography>
                  </CardContent>
                </Card>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <QrCodeScanner color="secondary" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6">{summary?.actualQuantity || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Actual Quantity
                    </Typography>
                  </CardContent>
                </Card>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <MonetizationOn color="success" sx={{ fontSize: 32, mb: 1 }} />
                    <Typography variant="h6">{getDisplayPrice(summary?.stockValue || 0, "PKR ")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Stock Value
                    </Typography>
                  </CardContent>
                </Card>
              </Grid2>

              <Grid2 size={{ xs: 12, sm: 6, md: 3 }}>
                <Card>
                  <CardContent sx={{ textAlign: "center" }}>
                    <CheckCircle
                      color={
                        summary?.shortAmount === 0 && summary?.excessAmount === 0
                          ? "success"
                          : (summary?.shortAmount ?? 0) > 0
                            ? "error"
                            : "warning"
                      }
                      sx={{ fontSize: 32, mb: 1 }}
                    />
                    <Typography variant="h6">{summary?.matchedCount || 0}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Matched Lots
                    </Typography>
                  </CardContent>
                </Card>
              </Grid2>
            </Grid2>

            {/* Progress Bar */}
            <Box sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2" color="text.secondary">
                  Scanning Progress
                </Typography>
                <Typography variant="body2" color="primary.main">
                  {summary?.scannedLots || 0}/{summary?.totalLots || 0} lots scanned
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={
                  summary?.totalLots && summary?.totalLots > 0
                    ? ((summary?.scannedLots ?? 0) / summary?.totalLots) * 100
                    : 0
                }
                sx={{ height: 6, borderRadius: 3 }}
              />
            </Box>

            {/* Discrepancy Alerts */}
            {((summary?.shortAmount ?? 0) > 0 || (summary?.excessAmount ?? 0) > 0) && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Discrepancies Found
                </Typography>
                <Grid2 container spacing={2}>
                  {(summary?.shortAmount ?? 0) > 0 && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Alert severity="warning">
                        <Typography variant="body2">
                          <strong>Short Amount:</strong> {summary?.shortCount} lots with {summary?.shortAmount} stock
                          value missing
                        </Typography>
                      </Alert>
                    </Grid2>
                  )}
                  {(summary?.excessAmount ?? 0) > 0 && (
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <Alert severity="info">
                        <Typography variant="body2">
                          <strong>Excess Amount:</strong> {summary?.excessCount} lots with {summary?.excessAmount} extra
                          stock value
                        </Typography>
                      </Alert>
                    </Grid2>
                  )}
                </Grid2>
              </Box>
            )}

            {/* Lot Status Summary */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Lot Status Summary
              </Typography>
              <Grid2 container spacing={2}>
                <Grid2 size={{ xs: 6, sm: 2.35 }}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <CheckCircle color="success" sx={{ fontSize: 24, mb: 1 }} />
                      <Typography variant="h6">{summary?.matchedCount ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Matched
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 2.35 }}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Warning color="warning" sx={{ fontSize: 24, mb: 1 }} />
                      <Typography variant="h6">{summary?.shortCount ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Short
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 2.35 }}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <TrendingUp color="info" sx={{ fontSize: 24, mb: 1 }} />
                      <Typography variant="h6">{summary?.excessCount ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Excess
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 2.35 }}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <Error color="error" sx={{ fontSize: 24, mb: 1 }} />
                      <Typography variant="h6">{summary?.remainingLots ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Remaining Lots
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid2>
                <Grid2 size={{ xs: 6, sm: 2.35 }}>
                  <Card>
                    <CardContent sx={{ textAlign: "center", py: 2 }}>
                      <CheckCircle color="success" sx={{ fontSize: 24, mb: 1 }} />
                      <Typography variant="h6">{summary?.scannedLots ?? 0}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Scanned Lots
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid2>
              </Grid2>
            </Box>

            {/* Search and Data Grid2 */}
            <Box sx={{ mb: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">Lot Details ({lots.length} lots)</Typography>
                <Button onClick={() => refetchLots()} variant="outlined" size="small" startIcon={<Refresh />}>
                  Refresh
                </Button>
              </Box>

              <TextField
                fullWidth
                placeholder="Search by Action ID..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
                sx={{ mb: 2 }}
              />
            </Box>

            {/* Data Grid2 */}
            <Card>
              <DataGridMui
                id="audit-product-lots-modal"
                data={lots}
                isLoading={lotsLoading}
                enableSorting={false}
                columns={columns}
                paginationModel={pagination}
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                rowsPerPageOptions={[10, 20, 50]}
                initialPageSize={10}
                skeletonCount={10}
                sortingModel={sorting as any}
                onSortModelChange={setSorting}
                onPaginationChange={setPagination}
                pageCount={lotsData?.getAllAuditLots?.meta?.totalCount ?? undefined}
              />
            </Card>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ProductLotsModal
